import { useEffect, useRef, useState } from "react";
import { Delete, ScanFace } from "lucide-react";

export const PIN_LENGTH = 6;

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "face", "0", "del"];

// Six-dot PIN entry with an on-screen keypad (and keyboard support on desktop).
export default function PinPad({ onComplete, onFaceId, busy = false, error = "", resetKey = 0 }) {
  const [pin, setPinState] = useState("");
  // Mirror of `pin` that's current even between renders, so fast taps aren't lost.
  const pinRef = useRef("");
  const setPin = (v) => {
    pinRef.current = v;
    setPinState(v);
  };

  useEffect(() => setPin(""), [resetKey]);

  const press = (k) => {
    if (busy) return;
    const current = pinRef.current;
    if (k === "del") return setPin(current.slice(0, -1));
    if (k === "face") return onFaceId?.();
    if (current.length >= PIN_LENGTH) return;
    const next = current + k;
    setPin(next);
    if (next.length === PIN_LENGTH) setTimeout(() => onComplete(next), 120);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") press("del");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className="flex flex-col items-center" data-testid="pin-pad">
      <div className={`flex gap-3 mb-3 ${error ? "nest-shake" : ""}`} key={resetKey}>
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <span
            key={i}
            className={`w-3.5 h-3.5 rounded-full border transition-colors ${
              i < pin.length
                ? "bg-nest-brand border-nest-brand"
                : "border-nest-faint bg-transparent"
            }`}
          />
        ))}
      </div>
      <div className="h-5 text-sm text-nest-warn mb-4" data-testid="pin-error">
        {error}
      </div>
      <div className="grid grid-cols-3 gap-4">
        {KEYS.map((k) => {
          if (k === "face" && !onFaceId) return <span key={k} />;
          const isDigit = /[0-9]/.test(k);
          return (
            <button
              key={k}
              type="button"
              onClick={() => press(k)}
              disabled={busy}
              aria-label={k === "del" ? "Delete" : k === "face" ? "Use Face ID" : k}
              data-testid={`pin-key-${k}`}
              className={`w-[72px] h-[72px] rounded-full flex items-center justify-center font-heading text-2xl select-none transition-colors ${
                isDigit
                  ? "nest-glass border border-nest-line active:bg-nest-surface-5"
                  : "text-nest-soft active:bg-nest-surface-2"
              } disabled:opacity-50`}
            >
              {k === "del" ? (
                <Delete size={22} strokeWidth={1.5} />
              ) : k === "face" ? (
                <ScanFace size={26} strokeWidth={1.5} />
              ) : (
                k
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
